"use client";

import { StaticImageData } from "next/image";
import { usePathname } from "next/navigation";
import Script from "next/script";
import { createContext, useContext, useEffect, useState } from "react";

const adManagerContext = createContext<object | undefined>({});

export interface AdConfig {
  unitId: string;
  elementId: string;
  width?: number;
  height?: number;
  minWidth?: number;
  sizes?: { width: number; height: number }[];
  static?: {
    images: {
      src: StaticImageData;
      minWidth: number;
      maxWidth: number;
    }[];
    href: string;
    alt: string;
  };
  noBackground?: boolean;
  noPadding?: boolean;
}

export interface AdConfigs {
  [key: string]: AdConfig;
}

export function tryLoadAds(ads: AdConfigs) {
  if (typeof window === "undefined" || !window.googletag) {
    // waiting for script to load
    return false;
  }

  window.googletag = window.googletag || { cmd: [] };
  window.googletag.cmd.push(function () {
    window.googletag.destroySlots();
    for (const ad of Object.values(ads)) {
      if (ad.static) {
        continue;
      }

      if (ad.sizes) {
        // Ref:  https://www.monterail.com/blog/gpt-ads-in-spa-next.js
        let mapping = window.googletag.sizeMapping();

        for (const size of ad.sizes) {
          mapping = mapping.addSize(
            [size.width, 0],
            [[size.width, size.height]],
          );
        }

        mapping = mapping.addSize([0, 0], []).build();

        window.googletag
          .defineSlot(
            ad.unitId,
            ad.sizes.map((size) => {
              return [size.width, size.height];
            }),
            ad.elementId,
          )
          .defineSizeMapping(mapping)
          .addService(window.googletag.pubads());
      } else {
        window.googletag
          .defineSlot(ad.unitId, [ad.width, ad.height], ad.elementId)
          .addService(window.googletag.pubads());
      }
    }
    window.googletag.pubads().enableSingleRequest();
    window.googletag.enableServices();
  });

  Object.values(ads).forEach((config) => {
    if (WillRender(config)) {
      window.googletag.cmd.push(function () {
        window.googletag.display(config.elementId);
      });
    }
  });

  return true;
}

export function WillRender(config: AdConfig) {
  return (
    !config.minWidth ||
    (typeof window !== "undefined" && window.innerWidth > config.minWidth)
  );
}

export function AdManagerProvider({
  ads,
  children,
}: Readonly<{ ads: AdConfigs; children: React.ReactNode }>) {
  const pathname = usePathname();
  const [loadedPathname, setLoadedPathname] = useState<string | null>(null);
  const contextValue: object = {};

  const handleTryLoadAds = () => {
    if (pathname !== loadedPathname && tryLoadAds(ads)) {
      setLoadedPathname(pathname);
    }
  };

  useEffect(handleTryLoadAds, [pathname, loadedPathname, ads]);

  return (
    <adManagerContext.Provider value={contextValue}>
      {children}
      <Script
        src="https://securepubads.g.doubleclick.net/tag/js/gpt.js"
        onLoad={handleTryLoadAds}
        async
        strategy="lazyOnload"
      />
    </adManagerContext.Provider>
  );
}

export function useAdManager() {
  const context = useContext(adManagerContext);
  if (!context) {
    throw new Error("useAdManager must be used within a AdManagerProvider");
  }
  return context;
}
